import { useEffect } from "react";
import { CopyableDate } from "../../CopyableDate.jsx";
import {
  Download,
  ExternalLink,
  Eye,
  FileText,
  LoaderCircle,
  X,
} from "lucide-react";

const dateTimeFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : dateTimeFormatter.format(date);
}

export function ActuacionDocumentDialog({
  dialog,
  busy,
  onClose,
  onSelectAttachment,
}) {
  if (!dialog) return null;
  const selecting = dialog.kind === "attachments";
  return (
    <div
      className="modal-backdrop document-dialog-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="document-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="icon-button dialog-close"
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <X size={17} />
        </button>
        <span className="document-dialog-icon">
          <FileText size={20} />
        </span>
        <h2 id="document-dialog-title">
          {selecting ? "Elegí un archivo" : dialog.title}
        </h2>
        {selecting ? (
          <>
            <p>
              Esta actuación tiene varios archivos. Seleccioná cuál querés
              abrir.
            </p>
            <div className="actuacion-attachment-list">
              {dialog.attachments.map((attachment) => (
                <button
                  type="button"
                  key={attachment.id}
                  disabled={Boolean(busy)}
                  onClick={() => onSelectAttachment(attachment)}
                >
                  <FileText size={17} />
                  <span>
                    <strong>{attachment.title}</strong>
                    {attachment.date && (
                      <small>
                        <CopyableDate as="span" value={attachment.date}>
                          {formatDate(attachment.date)}
                        </CopyableDate>
                      </small>
                    )}
                  </span>
                  {busy === attachment.id ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : (
                    <Eye size={16} />
                  )}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p>{dialog.message}</p>
            <div className="dialog-actions">
              <button className="button primary" type="button" onClick={onClose}>
                Entendido
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export function PdfViewer({ document, onClose }) {
  useEffect(() => {
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  if (!document) return null;
  return (
    <div className="pdf-viewer-overlay" role="dialog" aria-modal="true">
      <header>
        <div>
          <FileText size={19} />
          <span>
            <strong>{document.fileName}</strong>
            <small>Documento del Poder Judicial de Mendoza</small>
          </span>
        </div>
        <nav aria-label="Acciones del documento">
          <a
            className="button secondary"
            href={document.url}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink size={16} />
            Nueva pestaña
          </a>
          <a
            className="button secondary"
            href={document.url}
            download={document.fileName}
          >
            <Download size={16} />
            Descargar
          </a>
          <button
            className="icon-button pdf-viewer-close"
            type="button"
            onClick={onClose}
            aria-label="Cerrar documento"
          >
            <X size={19} />
          </button>
        </nav>
      </header>
      <iframe src={document.url} title={document.fileName} />
    </div>
  );
}
